import React, { useState, useEffect, useRef } from 'react';
import {
  Lightbulb,
  Zap,
  Target,
  Brain,
  Sparkles,
  ChevronRight,
  Plus,
  Clock,
  Play,
  X
} from 'lucide-react';
import FeatureStatusBadge from '../shared/FeatureStatusBadge';
import SectionControls from '../shared/SectionControls';

const IdeasDemoCompact = ({ data, onAnimationComplete, onContinue, isLastSection = false }) => {
  const [activeTab, setActiveTab] = useState('builder');
  const [isInView, setIsInView] = useState(false);
  const [showCoachTip, setShowCoachTip] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [wordCount, setWordCount] = useState(0);
  const [essayParagraphs, setEssayParagraphs] = useState([]);
  const [showQuestion2, setShowQuestion2] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coachPopupStep, setCoachPopupStep] = useState(0);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [animationsComplete, setAnimationsComplete] = useState(false);
  const [showBuildingIndicator, setShowBuildingIndicator] = useState(false);
  const [showOutline, setShowOutline] = useState(false);
  const [completedSections, setCompletedSections] = useState([]);
  const [turningPointActive, setTurningPointActive] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView && !hasPlayedOnce) {
          setIsInView(true);
          setHasPlayedOnce(true);
          setTimeout(() => startEssayBuildingDemo(), 1000);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView, hasPlayedOnce]);

  const startEssayBuildingDemo = () => {
    // Step 1: Show outline and first paragraph
    setTimeout(() => {
      setShowOutline(true);
      setEssayParagraphs([{
        id: 1,
        text: `"We're moving. Again." My mom said it so casually, like we were switching grocery stores instead of uprooting my entire life. I nodded, but inside I felt this heat rising—the kind that comes when you know the easy version of yourself won't survive what's coming next.`
      }]);
      setCompletedSections(['opening']);
      animateWordCount(0, 46);
    }, 1000);

    // Step 2: Question 2 appears on left
    setTimeout(() => {
      setShowQuestion2(true);
      setCurrentQuestion(2);
    }, 2500);

    // Step 3: Student starts typing, then backspaces
    setTimeout(() => {
      const textarea = document.querySelector('textarea[placeholder="Describe the turning point..."]');
      if (textarea) {
        let text = "It was really stressful...";
        let i = 0;
        const typeInterval = setInterval(() => {
          if (i <= text.length) {
            textarea.value = text.substring(0, i);
            i++;
          } else {
            clearInterval(typeInterval);

            setTimeout(() => {
              let j = text.length;
              const backspaceInterval = setInterval(() => {
                if (j >= 0) {
                  textarea.value = text.substring(0, j);
                  j--;
                } else {
                  clearInterval(backspaceInterval);
                }
              }, 40);
            }, 800);
          }
        }, 60);
      }
    }, 4500);

    // Step 4: Coach tip appears
    setTimeout(() => {
      setCoachPopupStep(1);
      setShowCoachTip(true);
    }, 6500);

    // Step 5: Student continues typing (meaningful answer)
    setTimeout(() => {
      const textarea = document.querySelector('textarea[placeholder="Describe the turning point..."]');
      if (textarea) {
        const newText = "This move dropped me into a school where everyone had grown up together, where friend groups were practically carved into stone. No one was looking for someone new to crowd their lunch table. I wasn't just trying to make friends; I was trying to matter in a place that had already decided who mattered.";

        let i = 0;
        const typeInterval = setInterval(() => {
          if (i <= newText.length) {
            textarea.value = newText.substring(0, i);
            i++;
          } else {
            clearInterval(typeInterval);
            // Simulate submit click
            setTimeout(() => {
              setIsSubmitting(true);
              setShowBuildingIndicator(true);
              // Add second paragraph after "submit"
              setTimeout(() => {
                setEssayParagraphs(prev => [...prev, {
                  id: 2,
                  text: newText
                }]);
                animateWordCount(46, 128);
                setIsSubmitting(false);
                setShowBuildingIndicator(false);
                setShowCoachTip(false);
                setCompletedSections(prev => [...prev, 'challenge']);
                setTurningPointActive(true);
                textarea.value = '';
                setShowQuestion2(false);
                // Turn off turning point badge after 2 seconds
                setTimeout(() => setTurningPointActive(false), 2000);
              }, 1000);
            }, 500);
          }
        }, 40);
      }
    }, 8500);

    // Mark complete
    setTimeout(() => {
      setAnimationsComplete(true);
      onAnimationComplete && onAnimationComplete();
    }, 13000);
  };

  const animateWordCount = (start, end) => {
    const increment = end > start ? 1 : -1;
    const steps = Math.abs(end - start);
    const duration = 1500;
    const stepDuration = duration / steps;

    let current = start;
    const interval = setInterval(() => {
      current += increment * 3;
      if ((increment > 0 && current >= end) || (increment < 0 && current <= end)) {
        setWordCount(end);
        clearInterval(interval);
      } else {
        setWordCount(current);
      }
    }, stepDuration);
  };

  const replayAnimation = () => {
    setWordCount(0);
    setEssayParagraphs([]);
    setShowQuestion2(false);
    setShowCoachTip(false);
    setCurrentQuestion(1);
    setCoachPopupStep(0);
    setAnimationsComplete(false);
    setIsSubmitting(false);
    setShowBuildingIndicator(false);
    setShowOutline(false);
    setCompletedSections([]);
    setTurningPointActive(false);

    const textarea = document.querySelector('textarea[placeholder="Describe the turning point..."]');
    if (textarea) textarea.value = '';

    setTimeout(() => startEssayBuildingDemo(), 100);
  };

  const tabs = [
    {
      id: 'builder',
      icon: Zap,
      title: 'Builder',
      description: 'AI-guided essay development',
      content: (
        <div className="w-full">
          {/* Compact Live Draft Header - Fixed width container */}
          <div className="bg-slate-800 text-white px-4 py-3 rounded-lg mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium">LIVE DRAFT</span>
                <span className="text-2xl font-bold">{wordCount}/650</span>
              </div>
              <div className="text-xs text-slate-400">Your essay builds as you write</div>
            </div>

            {/* Compact badges in single row */}
            <div className="flex gap-2">
              <span className={`px-2 py-0.5 text-white text-xs rounded-full transition-all ${
                wordCount >= 46 ? 'bg-teal-600' : 'bg-slate-600'
              }`}>
                {wordCount >= 46 ? '✓' : '•'} Strong opening
              </span>
              <span className={`px-2 py-0.5 text-white text-xs rounded-full transition-all ${
                wordCount >= 128 ? 'bg-teal-600' : 'bg-slate-600'
              }`}>
                {wordCount >= 128 ? '✓' : '•'} Personal voice
              </span>
              <span className={`px-2 py-0.5 text-xs rounded-full transition-all duration-500 ${
                turningPointActive
                  ? 'bg-amber-500 text-slate-900 animate-pulse scale-110'
                  : wordCount >= 128
                  ? 'bg-teal-600 text-white'
                  : 'bg-slate-600 text-white'
              }`}>
                {wordCount >= 128 ? '✓' : '•'} Turning point
              </span>
              <span className={`px-2 py-0.5 text-white text-xs rounded-full transition-all ${
                wordCount >= 185 ? 'bg-teal-600' : 'bg-slate-600'
              }`}>
                {wordCount >= 185 ? '✓' : '•'} Flow
              </span>
            </div>
          </div>

          {/* Two-column layout: Left = Questions/Interaction, Right = Essay Draft */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,1fr] gap-3 w-full">
            {/* Left Column: Outline, Questions and Coach Tip */}
            <div className="space-y-3 w-full min-w-0">
              {/* Compact Essay Outline - Always visible once started */}
              {showOutline && (
                <div className="bg-slate-50 border border-slate-200 p-2 rounded-lg">
                  <div className="text-xs font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
                    <Target className="w-3 h-3" />
                    STRUCTURE
                  </div>
                  <div className="flex gap-3 text-xs">
                    <div className={`flex items-center gap-1.5 ${
                      completedSections.includes('opening') ? 'text-emerald-600 font-medium' : 'text-slate-500'
                    }`}>
                      <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${
                        completedSections.includes('opening')
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-300'
                      }`}>
                        {completedSections.includes('opening') && (
                          <span className="text-xs">✓</span>
                        )}
                      </div>
                      <span>Opening</span>
                    </div>
                    <div className={`flex items-center gap-1.5 ${
                      completedSections.includes('challenge') ? 'text-emerald-600 font-medium' :
                      currentQuestion === 2 ? 'text-azure-600' : 'text-slate-500'
                    }`}>
                      <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${
                        completedSections.includes('challenge')
                          ? 'border-emerald-500 bg-emerald-50'
                          : currentQuestion === 2
                          ? 'border-azure-500 bg-azure-50 animate-pulse'
                          : 'border-slate-300'
                      }`}>
                        {completedSections.includes('challenge') && (
                          <span className="text-xs">✓</span>
                        )}
                      </div>
                      <span>Challenge</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-400">
                      <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300" />
                      <span>Growth</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-400">
                      <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300" />
                      <span>Forward</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Question 2 */}
              {showQuestion2 && (
                <div className="bg-azure-50 border border-azure-200 p-3 rounded-lg animate-fadeInScale">
                  <div className="text-xs font-medium text-azure-600 mb-1">QUESTION 2</div>
                  <div className="text-sm font-medium text-slate-800 mb-2">
                    What specific moment changed your perspective?
                  </div>
                  <textarea
                    placeholder="Describe the turning point..."
                    className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm h-20 resize-none"
                    disabled={isSubmitting}
                  />
                  <button
                    className={`mt-2 px-3 py-1.5 text-sm font-medium rounded transition-all ${
                      isSubmitting
                        ? 'bg-emerald-600 text-white'
                        : 'bg-azure-600 text-white hover:bg-azure-700'
                    }`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              )}

              {/* Coach Tip - Appears below question */}
              {showCoachTip && (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 animate-slideUp">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-amber-600" />
                    <div className="font-semibold text-sm text-amber-800">Coach's Tip</div>
                  </div>
                  <div className="text-sm text-amber-700 italic">
                    I don't want to hear 'it was stressful.' Tell me <span className="font-semibold">why</span> this
                    particular situation pushed you beyond what you thought you could handle.
                  </div>
                </div>
              )}

              {/* Next steps message after submission */}
              {completedSections.includes('challenge') && !showQuestion2 && (
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg animate-fadeIn">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-emerald-600" />
                    <div className="font-semibold text-sm text-emerald-700">Great progress!</div>
                  </div>
                  <div className="text-sm text-emerald-700">
                    Your essay is building nicely. Next, we'll explore how this experience changed you.
                  </div>
                </div>
              )}
            </div>

            {/* Right Column: Essay Draft Building */}
            <div className="bg-white border border-slate-200 rounded-lg w-full min-w-0">
              <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">Live Essay Draft</span>
                {showBuildingIndicator && (
                  <span className="text-xs text-emerald-600 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Building...
                  </span>
                )}
              </div>
              <div className="p-4 space-y-3">
                {essayParagraphs.map((paragraph, index) => (
                  <p key={paragraph.id} className={`leading-relaxed text-sm text-slate-800 animate-fadeIn`}>
                    {paragraph.text}
                  </p>
                ))}
                {essayParagraphs.length === 0 && (
                  <p className="text-sm text-slate-400 italic">Your essay will appear here as you answer questions...</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'topics',
      icon: Target,
      title: 'Topics',
      content: (
        <div className="grid gap-3">
          {/* Compact topic cards */}
          <div className="p-3 border border-slate-200 rounded-lg hover:border-azure-300 transition-all cursor-pointer">
            <div className="flex items-start justify-between mb-1">
              <h4 className="font-medium text-sm text-slate-900">Leadership Through Crisis</h4>
              <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">95%</span>
            </div>
            <p className="text-xs text-slate-600 mb-2">
              Your robotics team equipment failure shows resilience and problem-solving.
            </p>
            <div className="flex gap-1">
              <span className="text-xs bg-azure-100 text-azure-700 px-1.5 py-0.5 rounded">Engineering</span>
              <span className="text-xs bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">Leadership</span>
            </div>
          </div>

          <div className="p-3 border border-slate-200 rounded-lg hover:border-azure-300 transition-all cursor-pointer">
            <div className="flex items-start justify-between mb-1">
              <h4 className="font-medium text-sm text-slate-900">Community Impact Project</h4>
              <span className="text-xs bg-azure-100 text-azure-700 px-1.5 py-0.5 rounded">88%</span>
            </div>
            <p className="text-xs text-slate-600 mb-2">
              Your irrigation system demonstrates engineering with social consciousness.
            </p>
            <div className="flex gap-1">
              <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Innovation</span>
              <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Service</span>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'brainstorm',
      icon: Brain,
      title: 'Brainstorm',
      content: (
        <div className="grid lg:grid-cols-3 gap-3">
          <div className="bg-gradient-to-br from-violet-50 to-azure-50 p-3 rounded-lg border border-violet-200">
            <h4 className="font-medium text-sm text-slate-900 mb-2">Values</h4>
            <div className="space-y-1.5 text-xs text-slate-600">
              <div>• What drives you?</div>
              <div>• What problems do you notice?</div>
              <div>• When do others seek help?</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-3 rounded-lg border border-emerald-200">
            <h4 className="font-medium text-sm text-slate-900 mb-2">Stories</h4>
            <div className="space-y-1.5 text-xs text-slate-600">
              <div>• Failure that taught you</div>
              <div>• Moment of realization</div>
              <div>• Unexpected challenge</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-3 rounded-lg border border-orange-200">
            <h4 className="font-medium text-sm text-slate-900 mb-2">Vision</h4>
            <div className="space-y-1.5 text-xs text-slate-600">
              <div>• Impact you want</div>
              <div>• Problems to solve</div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div ref={sectionRef} className="max-w-7xl mx-auto px-4 w-full">
      {/* Compact Section Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 rounded-full">
            <Lightbulb className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">Ideas & Essay Development</span>
          </div>
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2">
          Turn Ideas into Essays That Matter
        </h2>
        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
          AI coaching that helps you find your authentic voice and build compelling essays.
        </p>
      </div>

      {/* Horizontal Tabs */}
      <div className="flex gap-2 mb-4 border-b border-slate-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium text-sm transition-all border-b-2 ${
              activeTab === tab.id
                ? 'text-azure-600 border-azure-600'
                : 'text-slate-600 border-transparent hover:text-slate-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <tab.icon className="w-4 h-4" />
              {tab.title}
            </div>
          </button>
        ))}
      </div>

      {/* Tab Content - Fixed width container */}
      <div className="bg-slate-50 rounded-lg p-4 w-full min-h-[400px]">
        {tabs.find(t => t.id === activeTab)?.content}
      </div>

      {/* Section Controls */}
      <SectionControls
        onReplay={replayAnimation}
        onContinue={onContinue}
        isLastSection={isLastSection}
        showControls={animationsComplete}
      />

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fadeInScale {
          animation: fadeInScale 0.3s ease-out forwards;
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out forwards;
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default IdeasDemoCompact;